import { AsyncPipe } from '@angular/common';
import type { OnInit } from '@angular/core';
import {
  Component,
  DestroyRef,
  inject,
  Injector,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import type { OnyxSuggestion } from '@onyx/angular';
import {
  OnyxDropdownComponent,
  OnyxFormGroupComponent,
  onyxMaxNumberValidator,
  onyxMinNumberValidator,
  OnyxSuggestionsComponent,
  OnyxTabsComponent,
  OnyxTextFieldComponent,
} from '@onyx/angular';
import { isEqual, pick } from 'lodash';
import {
  combineLatestWith,
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
} from 'rxjs';
import { DictionaryCode } from '../../../../../../common/enums/dictionary-code';
import { InvoiceType } from '../../../../../../common/enums/invoice-type';
import { ValidationHelper } from '../../../../../../common/helpers/validation.helper';
import { DictionariesService } from '../../../../../../common/services/dictionaries.service';
import { CollectiveInvoiceIssueTerm } from '../../../common/enums/collective-invoice-issue-term';
import { ContractorShippingTypeFormComponent } from '../../contractor-shipping-type-form/contractor-shipping-type-form.component';

export type ContractorInvoiceFormGroup = ReturnType<
  typeof ContractorInvoiceFormComponent.buildForm
>;
export type ContractorInvoiceForm = ReturnType<
  ContractorInvoiceFormGroup['getRawValue']
>;

@Component({
  selector: 'app-contractor-invoice-form',
  imports: [
    ContractorShippingTypeFormComponent,
    OnyxTabsComponent,
    AsyncPipe,
    ReactiveFormsModule,
    OnyxDropdownComponent,
    OnyxFormGroupComponent,
    OnyxSuggestionsComponent,
    OnyxTextFieldComponent,
  ],
  templateUrl: './contractor-invoice-form.component.html',
  styleUrl: './contractor-invoice-form.component.scss',
})
export class ContractorInvoiceFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  public readonly form = input.required<ContractorInvoiceFormGroup>();
  public readonly country = input.required<string | null>();

  protected readonly I18N = 'contractors.contractorForm.invoices';

  protected readonly invoiceTypes$ = this.dictionariesService.getDictionary(
    DictionaryCode.INVOICE_TYPE,
  );
  protected readonly countries$ = this.dictionariesService.getDictionary(
    DictionaryCode.COUNTRY,
  );
  protected readonly currencies$ = this.dictionariesService.getDictionary(
    DictionaryCode.CURRENCY,
  );
  protected readonly languages$ = this.dictionariesService.getDictionary(
    DictionaryCode.LANGUAGE,
  );
  protected readonly collectiveInvoiceIssuesTerms$ =
    this.dictionariesService.getDictionary(
      DictionaryCode.COLLECTIVE_INVOICE_ISSUE_TERM,
    );

  protected isSelfBiling = signal(false);
  protected languageSuggestions = signal<OnyxSuggestion<string>[]>([]);

  public ngOnInit(): void {
    const controls = this.form().controls;

    const valueChanges$ = this.form().valueChanges.pipe(
      startWith(undefined),
      map(() => this.form().getRawValue()),
      shareReplay(1),
      takeUntilDestroyed(this.destroyRef),
    );

    valueChanges$
      .pipe(
        map((value) =>
          pick(value, ['defaultType', 'collectiveInvoiceIssueTerm']),
        ),
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ defaultType, collectiveInvoiceIssueTerm }) => {
        ValidationHelper.toggleControls(controls.collectiveInvoiceIssueTerm, {
          enabled: defaultType === InvoiceType.COLLECTIVE,
          reset: true,
        });
        ValidationHelper.toggleControls(
          controls.collectiveInvoiceIssueSelectedDay,
          {
            enabled:
              collectiveInvoiceIssueTerm ===
              CollectiveInvoiceIssueTerm.SELECTED_DAY,
            reset: true,
          },
        );

        this.isSelfBiling.set(defaultType === InvoiceType.SELF_BILLING);
        ValidationHelper.toggleControls(
          [
            controls.primaryCurrency,
            controls.primaryLanguage,
            controls.shippingType,
          ],
          { enabled: !this.isSelfBiling(), reset: true },
        );
      });

    toObservable(this.country, { injector: this.injector })
      .pipe(
        combineLatestWith(this.countries$),
        map(([country, countries]) =>
          countries.find((option) => option.value === country),
        ),
        filter((country) => !!country),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ currency, languages }) => {
        if (currency && !controls.primaryCurrency.getRawValue()) {
          controls.primaryCurrency.setValue(currency);
        }
        if (!languages?.length) return;

        if (languages.length === 1 && !controls.primaryLanguage.getRawValue()) {
          controls.primaryLanguage.setValue(languages[0]);
        } else if (languages.length > 1) {
          this.languageSuggestions.set(
            languages.map((value) => ({
              name: `${DictionaryCode.LANGUAGE}.${value}`,
              value,
            })),
          );
        }
      });
  }

  public static buildForm(fb: NonNullableFormBuilder, branchUuid: string) {
    return fb.group({
      branchUuid: fb.control(branchUuid, [Validators.required]),
      defaultType: fb.control<InvoiceType | null>(InvoiceType.SINGLE, [
        Validators.required,
      ]),
      collectiveInvoiceIssueTerm: fb.control<CollectiveInvoiceIssueTerm | null>(
        { value: null, disabled: true },
        [Validators.required],
      ),
      collectiveInvoiceIssueSelectedDay: fb.control<number | null>(
        { value: null, disabled: true },
        [
          Validators.required,
          onyxMinNumberValidator(1),
          onyxMaxNumberValidator(28),
        ],
      ),
      shippingType: ContractorShippingTypeFormComponent.buildForm(fb),
      primaryCurrency: fb.control<string | null>(null, [Validators.required]),
      primaryLanguage: fb.control<string | null>(null, [Validators.required]),
    });
  }
}
